import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { Session } from '../../api/session';
import { createMessageToMC, type ObjectInfo } from '../../api/tenant';
import { describePassError } from '../../domain/passErrors';
import type { TelegramApi } from '../../telegram/webapp';
import f from '../pass-form/passForm.module.css';

/** New message to the managing company: object + subject + text, request type 4 (spec §6.7). */
export function NewMessageScreen({ session, telegram, object }: { session: Session; telegram: TelegramApi; object: ObjectInfo }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: () => createMessageToMC(session, { objectId: object.id, title: title.trim(), message: message.trim() }),
    onSuccess: (created) => {
      telegram.notify('success');
      void queryClient.invalidateQueries({ queryKey: ['requests'] });
      navigate(`/requests/${created.id}`, { replace: true });
    },
    onError: (e) => {
      telegram.notify('error');
      setError(describePassError(e).general);
    },
  });

  return (
    <main className={f.sheet} aria-labelledby="new-message-title">
      <header className={f.head}>
        <h1 id="new-message-title" className={f.title}>
          Сообщение в УК
        </h1>
        <p className={f.where}>{object.name}</p>
      </header>
      <div className={f.field}>
        <label htmlFor="msg-title" className={f.label}>
          Тема
        </label>
        <input id="msg-title" className={f.input} value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} placeholder="Например, не открывается шлагбаум" />
      </div>
      <div className={f.field}>
        <label htmlFor="msg-text" className={f.label}>
          Сообщение
        </label>
        <textarea id="msg-text" className={f.textarea} value={message} rows={6} maxLength={4000} onChange={(e) => setMessage(e.target.value)} />
      </div>
      <div className={f.footer}>
        {error && (
          <p className={f.error} role="alert">
            {error}
          </p>
        )}
        <button
          type="button"
          className={f.submit}
          disabled={send.isPending}
          onClick={() => {
            if (!title.trim() || !message.trim()) return setError('Заполните тему и сообщение');
            setError(null);
            send.mutate();
          }}
        >
          {send.isPending ? 'Отправляем…' : 'Отправить в УК'}
        </button>
      </div>
    </main>
  );
}
